import React, { useState } from 'react';
import { FiX, FiAlertTriangle } from 'react-icons/fi';

const CancelLineItemModal = ({ isOpen, onClose, lineItem, onConfirmCancel, isLoading }) => {
  const [cancelReason, setCancelReason] = useState('');
  const [cancelQuantity, setCancelQuantity] = useState('');

  const handleConfirm = () => {
    const qty = parseInt(cancelQuantity) || lineItem?.qtyOnHold || 0;
    if (!cancelReason.trim()) {
      return; // Don't allow empty reason
    }
    if (qty <= 0 || qty > (lineItem?.qtyOnHold || 0)) {
      return; // Invalid quantity
    }
    onConfirmCancel(cancelReason.trim(), qty);
  };

  const handleClose = () => {
    setCancelReason('');
    setCancelQuantity('');
    onClose();
  };

  if (!isOpen) return null;

  const maxCancelQty = lineItem?.qtyOnHold || 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center" role="dialog" aria-modal="true" aria-labelledby="cancel-modal-title">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 id="cancel-modal-title" className="text-xl font-bold text-gray-900">Cancel/Delete Items</h2>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-800" aria-label="Close dialog">
            <FiX size={24} />
          </button>
        </div>
        
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-start">
            <FiAlertTriangle className="text-red-600 mr-2 mt-0.5" size={20} />
            <div>
              <p className="text-sm text-gray-700 mb-2">
                You are about to cancel/delete items from hold:
              </p>
              <div className="text-sm font-medium text-gray-900">
                <p><strong>SKU:</strong> {lineItem?.productSku}</p>
                <p><strong>Product:</strong> {lineItem?.productName}</p>
                <p><strong>Currently on Hold:</strong> {maxCancelQty}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quantity to Cancel <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min="1"
            max={maxCancelQty}
            value={cancelQuantity}
            onChange={(e) => setCancelQuantity(e.target.value)}
            placeholder={`Max: ${maxCancelQty}`}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            disabled={isLoading}
          />
          <p className="text-xs text-gray-500 mt-1">
            Leave empty to cancel all {maxCancelQty} items
          </p>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cancellation Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Please provide a reason for cancelling these items (e.g., damaged, missing, customer request)..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            rows={3}
            maxLength={500}
            disabled={isLoading}
          />
          <p className="text-xs text-gray-500 mt-1">
            {cancelReason.length}/500 characters
          </p>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
            disabled={isLoading || !cancelReason.trim() || (!cancelQuantity && maxCancelQty === 0)}
          >
            {isLoading ? 'Processing...' : 'Cancel Items'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelLineItemModal; 